import { CircularProgress, Box } from '@mui/material';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';

import { ProtectedRoute } from './ProtectedRoute';
import { ROUTES } from './routes';

const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

const withSuspense = (Component: React.LazyExoticComponent<() => JSX.Element>) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

// Auth pages
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));

// Dashboard pages
const DevicesPage = lazy(() => import('@/pages/DevicesPage'));
const DeviceDetailPage = lazy(() => import('@/pages/DeviceDetailPage'));
const DeviceAddPage = lazy(() => import('@/pages/DeviceAddPage'));
const DeviceEditPage = lazy(() => import('@/pages/DeviceEditPage'));

const ApplicationsPage = lazy(() => import('@/pages/ApplicationsPage'));
const ApplicationDetailPage = lazy(() => import('@/pages/ApplicationDetailPage'));
const ApplicationAddPage = lazy(() => import('@/pages/ApplicationAddPage'));
const ApplicationEditPage = lazy(() => import('@/pages/ApplicationEditPage'));
const ApplicationSharePage = lazy(() => import('@/pages/ApplicationSharePage'));

const GatewaysPage = lazy(() => import('@/pages/GatewaysPage'));
const GatewayDetailPage = lazy(() => import('@/pages/GatewayDetailPage'));
const GatewayAddPage = lazy(() => import('@/pages/GatewayAddPage'));
const GatewayEditPage = lazy(() => import('@/pages/GatewayEditPage'));
const GatewayPoweroffPage = lazy(() => import('@/pages/GatewayPoweroffPage'));
const GatewaySpectralPage = lazy(() => import('@/pages/GatewaySpectralPage'));
const GatewayRncPage = lazy(() => import('@/pages/GatewayRncPage'));

const GatewayProfileDetailPage = lazy(() => import('@/pages/GatewayProfileDetailPage'));
const GatewayProfileAddPage = lazy(() => import('@/pages/GatewayProfileAddPage'));
const GatewayProfileEditPage = lazy(() => import('@/pages/GatewayProfileEditPage'));

const MulticastPage = lazy(() => import('@/pages/MulticastPage'));
const MulticastDetailPage = lazy(() => import('@/pages/MulticastDetailPage'));
const MulticastAddPage = lazy(() => import('@/pages/MulticastAddPage'));
const MulticastEditPage = lazy(() => import('@/pages/MulticastEditPage'));

const UsersPage = lazy(() => import('@/pages/UsersPage'));
const UserDetailPage = lazy(() => import('@/pages/UserDetailPage'));
const UserAddPage = lazy(() => import('@/pages/UserAddPage'));
const UserEditPage = lazy(() => import('@/pages/UserEditPage'));

const UserProfilesPage = lazy(() => import('@/pages/UserProfilesPage'));
const UserProfileDetailPage = lazy(() => import('@/pages/UserProfileDetailPage'));
const UserProfileAddPage = lazy(() => import('@/pages/UserProfileAddPage'));
const UserProfileEditPage = lazy(() => import('@/pages/UserProfileEditPage'));

const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const ConsolePage = lazy(() => import('@/pages/ConsolePage'));
const StatisticsPage = lazy(() => import('@/pages/StatisticsPage'));
const AiPage = lazy(() => import('@/pages/AiPage'));
const ImportStatusPage = lazy(() => import('@/pages/ImportStatusPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to={ROUTES.DEVICES} replace />,
  },
  {
    element: <AuthLayout />,
    children: [
      { path: ROUTES.LOGIN, element: withSuspense(LoginPage) },
      { path: ROUTES.FORGOT_PASSWORD, element: withSuspense(ForgotPasswordPage) },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: ROUTES.DEVICES, element: withSuspense(DevicesPage) },
          { path: ROUTES.DEVICE_ADD, element: withSuspense(DeviceAddPage) },
          { path: ROUTES.DEVICE_DETAIL, element: withSuspense(DeviceDetailPage) },
          { path: ROUTES.DEVICE_EDIT, element: withSuspense(DeviceEditPage) },

          { path: ROUTES.APPLICATIONS, element: withSuspense(ApplicationsPage) },
          { path: ROUTES.APPLICATION_ADD, element: withSuspense(ApplicationAddPage) },
          { path: ROUTES.APPLICATION_DETAIL, element: withSuspense(ApplicationDetailPage) },
          { path: ROUTES.APPLICATION_EDIT, element: withSuspense(ApplicationEditPage) },
          { path: ROUTES.APPLICATION_SHARE, element: withSuspense(ApplicationSharePage) },

          { path: ROUTES.GATEWAYS, element: withSuspense(GatewaysPage) },
          { path: ROUTES.GATEWAY_ADD, element: withSuspense(GatewayAddPage) },
          { path: ROUTES.GATEWAY_DETAIL, element: withSuspense(GatewayDetailPage) },
          { path: ROUTES.GATEWAY_EDIT, element: withSuspense(GatewayEditPage) },
          { path: ROUTES.GATEWAY_POWEROFF, element: withSuspense(GatewayPoweroffPage) },
          { path: ROUTES.GATEWAY_SPECTRAL, element: withSuspense(GatewaySpectralPage) },
          { path: ROUTES.GATEWAY_RNC, element: withSuspense(GatewayRncPage) },

          { path: ROUTES.GATEWAY_PROFILE_ADD, element: withSuspense(GatewayProfileAddPage) },
          { path: ROUTES.GATEWAY_PROFILE_DETAIL, element: withSuspense(GatewayProfileDetailPage) },
          { path: ROUTES.GATEWAY_PROFILE_EDIT, element: withSuspense(GatewayProfileEditPage) },

          { path: ROUTES.MULTICAST, element: withSuspense(MulticastPage) },
          { path: ROUTES.MULTICAST_ADD, element: withSuspense(MulticastAddPage) },
          { path: ROUTES.MULTICAST_DETAIL, element: withSuspense(MulticastDetailPage) },
          { path: ROUTES.MULTICAST_EDIT, element: withSuspense(MulticastEditPage) },

          { path: ROUTES.USERS, element: withSuspense(UsersPage) },
          { path: ROUTES.USER_ADD, element: withSuspense(UserAddPage) },
          { path: ROUTES.USER_DETAIL, element: withSuspense(UserDetailPage) },
          { path: ROUTES.USER_EDIT, element: withSuspense(UserEditPage) },

          { path: ROUTES.USER_PROFILES, element: withSuspense(UserProfilesPage) },
          { path: ROUTES.USER_PROFILE_ADD, element: withSuspense(UserProfileAddPage) },
          { path: ROUTES.USER_PROFILE_DETAIL, element: withSuspense(UserProfileDetailPage) },
          { path: ROUTES.USER_PROFILE_EDIT, element: withSuspense(UserProfileEditPage) },

          { path: ROUTES.PROFILE, element: withSuspense(ProfilePage) },
          { path: ROUTES.CHANGE_PASSWORD, element: withSuspense(ChangePasswordPage) },
          { path: ROUTES.CONSOLE, element: withSuspense(ConsolePage) },
          { path: ROUTES.STATISTICS, element: withSuspense(StatisticsPage) },
          { path: ROUTES.AI, element: withSuspense(AiPage) },
          { path: ROUTES.IMPORT_STATUS, element: withSuspense(ImportStatusPage) },
        ],
      },
    ],
  },
  { path: '*', element: withSuspense(NotFoundPage) },
]);
